import * as React from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import type { LeagueTransferStats } from 'types/fpl/transfers'
import { formatDecimal } from 'utils/numbers'
import { colors } from 'constants/colors'

interface Props {
  stats: LeagueTransferStats | null
  totalTeams: number
}

type TileAccent = {
  gradient: string
  accent: string
  valueColor?: string
}

const teamLabel = (count: number) => (count === 1 ? '1 team' : `${count} teams`)

const StatTile: React.FC<{
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  accent: TileAccent
  isMobile: boolean
  isDark: boolean
}> = ({ label, value, sub, accent, isMobile, isDark }) => (
  <Box
    sx={{
      position: 'relative',
      flex: '1 1 0',
      minWidth: isMobile ? 'calc(50% - 6px)' : 0,
      borderRadius: 2,
      px: { xs: 1.25, sm: 1.5 },
      py: { xs: 1.1, sm: 1.35 },
      background: accent.gradient,
      border: '1px solid',
      borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,34,41,0.08)',
      boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.22)' : '0 2px 8px rgba(0,34,41,0.06)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.35,
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 64,
        height: 64,
        borderRadius: '50%',
        background: accent.accent,
        opacity: isDark ? 0.12 : 0.16,
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: 3,
        width: '100%',
        background: `linear-gradient(90deg, ${accent.accent} 0%, transparent 65%)`,
        opacity: 0.7,
        pointerEvents: 'none',
      }}
    />
    <Typography
      sx={{
        position: 'relative',
        zIndex: 1,
        fontFamily: 'PremierLeagueBold',
        fontSize: isMobile ? '0.52rem' : '0.58rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'text.secondary',
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        position: 'relative',
        zIndex: 1,
        fontFamily: 'PremierLeagueBold',
        fontSize: isMobile ? '1.15rem' : '1.35rem',
        lineHeight: 1.1,
        color: accent.valueColor ?? (isDark ? 'primary.light' : 'text.primary'),
      }}
    >
      {value}
    </Typography>
    {sub ? (
      <Typography
        sx={{
          position: 'relative',
          zIndex: 1,
          fontFamily: 'PremierLeagueRegular',
          fontSize: isMobile ? '0.62rem' : '0.68rem',
          color: 'text.secondary',
          lineHeight: 1.2,
        }}
      >
        {sub}
      </Typography>
    ) : null}
  </Box>
)

const TransfersSummary: React.FC<Props> = ({ stats, totalTeams }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isDark = theme.palette.mode === 'dark'

  if (!stats) return null

  const leagueSize = totalTeams || 0
  const avgMinus = stats.avg_hit_cost ?? 0
  const distribution = stats.penalty_cost_distribution ?? []
  const hasMinuses = stats.penalties_count > 0

  const tileBg = (tint: string, tintDark: string) =>
    isDark
      ? `linear-gradient(135deg, ${tintDark} 0%, rgba(0,34,41,0.94) 100%)`
      : `linear-gradient(135deg, ${tint} 0%, rgba(255,255,255,0.96) 100%)`

  const tiles: {
    key: string
    label: string
    value: React.ReactNode
    sub?: React.ReactNode
    accent: TileAccent
  }[] = [
    {
      key: 'rolled',
      label: 'Rolled a transfer',
      value: stats.save_transfer_count,
      sub: leagueSize ? `${formatDecimal(stats.save_transfer_pct)}% of league` : undefined,
      accent: {
        gradient: tileBg('rgba(49,175,180,0.28)', 'rgba(49,175,180,0.32)'),
        accent: colors.MOONSTONE,
      },
    },
    {
      key: 'avg',
      label: 'Avg. transfers',
      value: formatDecimal(stats.avg_transfers),
      sub: 'per team this GW',
      accent: {
        gradient: tileBg('rgba(100,149,237,0.26)', 'rgba(100,149,237,0.3)'),
        accent: '#5B7FD1',
      },
    },
    {
      key: 'minus',
      label: 'Avg. minus',
      value: avgMinus > 0 ? `-${formatDecimal(avgMinus)}` : '0',
      sub: hasMinuses && leagueSize
        ? `${formatDecimal(stats.penalties_pct)}% took a minus`
        : 'No paid transfers',
      accent: {
        gradient: hasMinuses
          ? tileBg('rgba(180,54,49,0.2)', 'rgba(180,54,49,0.28)')
          : tileBg('rgba(0,34,41,0.06)', 'rgba(255,255,255,0.04)'),
        accent: hasMinuses ? colors.CARMINE_RED : isDark ? '#6dbfc4' : '#8a8a8a',
        valueColor: hasMinuses ? colors.CARMINE_RED : undefined,
      },
    },
  ]

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
        {tiles.map((tile) => (
          <StatTile
            key={tile.key}
            label={tile.label}
            value={tile.value}
            sub={tile.sub}
            accent={tile.accent}
            isMobile={isMobile}
            isDark={isDark}
          />
        ))}
      </Box>

      {distribution.length > 0 ? (
        <Box>
          <Typography
            sx={{
              fontFamily: 'PremierLeagueBold',
              fontSize: isMobile ? '0.58rem' : '0.62rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'text.secondary',
              mb: 0.75,
            }}
          >
            Minus breakdown
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65 }}>
            {distribution.map((bucket) => {
              const pct = leagueSize ? (bucket.count / leagueSize) * 100 : 0
              return (
                <Box
                  key={bucket.points}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.75,
                    background: isDark ? 'rgba(180,54,49,0.1)' : 'rgba(180,54,49,0.06)',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(180,54,49,0.22)' : 'rgba(180,54,49,0.14)',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'PremierLeagueBold',
                      fontSize: isMobile ? '0.88rem' : '0.95rem',
                      color: colors.CARMINE_RED,
                      minWidth: 36,
                      lineHeight: 1,
                    }}
                  >
                    −{bucket.points}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'PremierLeagueRegular',
                      fontSize: isMobile ? '0.72rem' : '0.78rem',
                      lineHeight: 1.3,
                      color: 'text.primary',
                    }}
                  >
                    {teamLabel(bucket.count)}
                    {leagueSize ? (
                      <>
                        {' · '}
                        <Box component="span" sx={{ fontFamily: 'PremierLeagueBold' }}>
                          {formatDecimal(pct)}%
                        </Box>
                        {' of league'}
                      </>
                    ) : null}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

export default TransfersSummary
